import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';

// @ts-expect-error - no TypeScript definitions yet
import { IconArrowCollapseRight } from 'src/assets/icons/ArrowCollapseRight';
import { CustomIconButton } from 'src/components/CustomIconButton';
// @ts-expect-error - no TypeScript definitions yet
import { DocumentInsightsPanel, TEXT_EDITOR_TYPE } from 'src/components/Insights';
// @ts-expect-error - no TypeScript definitions yet
import { VariationFormat } from 'src/components/VariationFormat';
import { BLOCKED_REASON } from 'src/data/common';
import type { DocumentVariation } from 'src/data/document';
import { slateToText } from 'src/lib/slate';
// @ts-expect-error - no TypeScript definitions yet
import { usePrepareDocumentInsightsProps } from 'src/services/insights';

import { useBenchmarkConfigProps } from '../../services/useBenchmarkConfigProps';
import { useReadonlyDocumentScoresActions } from '../../store';

import { type FetchDocumentVariationDetails, useDetailsData } from './useDetailsData';
import styles from './VariationDetails.module.scss';

interface VariationDetailsProps {
  className?: string;
  variation: DocumentVariation;
  portalElement: HTMLElement;
  benchmarkProjectId: string;
  fetchDetails: FetchDocumentVariationDetails;
}

export const VariationDetails = ({
  className,
  variation,
  portalElement,
  benchmarkProjectId,
  fetchDetails,
}: VariationDetailsProps) => {
  const actions = useReadonlyDocumentScoresActions();

  const text = useMemo(() => slateToText(variation.editorValue), [variation]);
  const locked = Boolean(variation.isScoreLocked || variation.performancePredictionsScoreLocked);

  const {
    scoreData,
    histogramsData,
    blockedReason,
    annotationsData,
    sensitivityData,
    audienceData,
  } = useDetailsData({ variation, text, fetchDetails });
  const benchmarkConfigProps = useBenchmarkConfigProps({
    variation,
    projectId: benchmarkProjectId,
  });

  const {
    tabProps,
    copyInputProps,
    scoreDetailsProps,
    scoreAdditionalFactorsProps,
    histogramAgeScoreProps,
    histogramGenderScoreProps,
    talkingPointsProps,
    channelPolicyProps,
    audienceChartProps,
    benchmarkProps,
  } = usePrepareDocumentInsightsProps({
    assetType: variation.assetType,
    generationTool: variation.generationTool,
    scoreData,
    histogramsData,
    annotationsData,
    secondaryScoringMethodName: variation.secondaryScoringMethodName,
    sensitivityData,
    editorContent: variation.editorValue,
    blockedReason: locked ? BLOCKED_REASON.limitedScoring : blockedReason,
    scoringMethodName: variation.scoringMethod?.name,
    campaignKeywords: variation.campaignKeywords,
    textEditorType: TEXT_EDITOR_TYPE.richSlate,
    isScoreLocked: locked,
    forceDisabled: true,
    readOnly: true,
    targetAudienceProps: audienceData,
    benchmarkConfigProps,
  });

  // The panel shows every section at once. A tab change sends an authenticated event, and a public visitor has no session.
  const readonlyTabProps = useMemo(() => ({ ...tabProps, showTabs: false }), [tabProps]);

  const handleCollapseClick = () => actions.closeVariationDetailsPanel();

  return createPortal(
    <>
      <div
        className={styles.overlay}
        onClick={handleCollapseClick}
      />
      <div
        className={cx(styles.root, className)}
        data-testid="VariationDetailsView"
      >
        <div className={styles.header}>
          <VariationFormat
            className={styles.variationFormat}
            assetType={variation.assetType}
            generationTool={variation.generationTool}
            mixtureSubAssetType={variation.mixtureSubAssetType}
            showInsightsLabel
          />

          <CustomIconButton
            className={styles.buttonCollapse}
            onClick={handleCollapseClick}
            data-testid="variationDetailsViewCollapseButton"
          >
            <IconArrowCollapseRight />
          </CustomIconButton>
        </div>

        <DocumentInsightsPanel
          className={styles.textDetails}
          tabProps={readonlyTabProps}
          copyInputProps={copyInputProps}
          scoreDetailsProps={scoreDetailsProps}
          scoreAdditionalFactorsProps={scoreAdditionalFactorsProps}
          audienceChartProps={audienceChartProps}
          histogramAgeScoreProps={histogramAgeScoreProps}
          histogramGenderScoreProps={histogramGenderScoreProps}
          talkingPointsProps={talkingPointsProps}
          channelPolicyProps={channelPolicyProps}
          benchmarkProps={benchmarkProps}
        />
      </div>
    </>,
    portalElement
  );
};
